import { Quaternion, Vector3 } from 'three';

import {
  LOCATE_MAX_MATCH_AREA_CM2,
  isMatchAreaWithinBounds,
  isUnboundedMaxMatchAreaCm2,
  type MatchAreaBounds,
} from '@sb/ai/matchArea';
import {
  cartesianPoseToMatrix4,
  getPlaneNormal,
  getPoseFromRelativeWristPose,
  movePlaneInItsZDirection,
  type CartesianPose,
  type Plane,
} from '@sb/geometry';
import type {
  CameraIntrinsics,
  ExtrinsicsKinematics,
} from '@sb/integrations/types/cameraTypes';

import type { HasContour } from './blobGeometry';

/**
 * What turns a match's pixel shape into an area on the surface it sits on: the
 * camera that took the frame, and the plane to measure on.
 */
export interface MatchAreaProjection {
  plane: Plane;
  cameraPose: CartesianPose;
  intrinsics: CameraIntrinsics;
}

type MatchShape = {
  detection: {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
  };
  segment?: HasContour | null;
};

/**
 * Where the camera was when the frame was taken. Undefined for a TCP-mounted
 * camera without the wrist pose the frame was taken at.
 */
export function cameraPoseAtCapture(
  extrinsics: ExtrinsicsKinematics,
  wristPoseAtCapture: CartesianPose | undefined,
): CartesianPose | undefined {
  if (extrinsics.kind === 'base') return extrinsics.extrinsics;
  if (!wristPoseAtCapture) return undefined;

  return getPoseFromRelativeWristPose(
    wristPoseAtCapture,
    extrinsics.extrinsics,
  );
}

/**
 * The ManualPlane measuring surface: the taught plane raised by the object
 * height, the top face the poses land on.
 */
export function raisedPlane(plane: Plane, objectHeight: number): Plane {
  return movePlaneInItsZDirection(plane, objectHeight);
}

/**
 * The surface a depth-projected match sits on: through the pose's position,
 * normal to its X axis. Locate poses approach along +X, into the surface, on
 * every projection method — the fitted normal for DetectedPlane, world -Z for
 * DetectedDepth and the top-down fallback.
 */
export function surfacePlaneAtPose(pose: CartesianPose): Plane {
  const orientation = new Quaternion(pose.i, pose.j, pose.k, pose.w);
  const origin = new Vector3(pose.x, pose.y, pose.z);

  return {
    origin,
    plusX: new Vector3(0, 1, 0).applyQuaternion(orientation).add(origin),
    plusY: new Vector3(0, 0, 1).applyQuaternion(orientation).add(origin),
  };
}

/** The match's outline in image pixels: its contour, else its box corners. */
function matchOutline(
  match: MatchShape,
): ReadonlyArray<{ x: number; y: number }> {
  if (match.segment != null && match.segment.contour.length >= 3) {
    return match.segment.contour;
  }

  const { x, y, width, height, rotation = 0 } = match.detection;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  return [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ].map(([sx, sy]) => {
    const dx = (sx * width) / 2;
    const dy = (sy * height) / 2;

    return { x: x + cos * dx - sin * dy, y: y + sin * dx + cos * dy };
  });
}

/**
 * A match's area on the projection plane, in cm². Each outline point is cast
 * through the pinhole model (lens distortion ignored, as the teach overlay
 * does) and intersected with the plane.
 *
 * Undefined when any point misses the plane — a ray parallel to it, or one
 * meeting it behind the camera — since the area would be meaningless.
 */
export function matchAreaCm2(
  match: MatchShape,
  { plane, cameraPose, intrinsics }: MatchAreaProjection,
): number | undefined {
  const cameraMatrix = cartesianPoseToMatrix4(cameraPose);
  const cameraOrigin = new Vector3(cameraPose.x, cameraPose.y, cameraPose.z);
  const normal = getPlaneNormal(plane);

  const originToPlane = normal.dot(
    new Vector3().subVectors(plane.origin, cameraOrigin),
  );

  const points: Vector3[] = [];

  for (const { x, y } of matchOutline(match)) {
    const ray = new Vector3(
      (x - intrinsics.ppx) / intrinsics.fx,
      (y - intrinsics.ppy) / intrinsics.fy,
      1,
    ).transformDirection(cameraMatrix);

    const rayDotNormal = ray.dot(normal);

    if (Math.abs(rayDotNormal) < 1e-9) return undefined;

    const distance = originToPlane / rayDotNormal;

    if (distance <= 0) return undefined;

    points.push(cameraOrigin.clone().addScaledVector(ray, distance));
  }

  // Shoelace on the plane: the points are coplanar, so the summed cross
  // products are parallel to the normal and their projection on it is twice
  // the enclosed area.
  const twiceArea = new Vector3();

  for (let i = 0; i < points.length; i += 1) {
    twiceArea.add(
      new Vector3().crossVectors(points[i], points[(i + 1) % points.length]),
    );
  }

  const areaM2 = Math.abs(twiceArea.dot(normal)) / 2;

  return areaM2 * 1e4;
}

/**
 * The bounds a filter built by {@link makeMatchAreaFilter} applies, for logs.
 * A missing bound is the open one.
 */
export function describeMatchAreaBounds(bounds: MatchAreaBounds): string {
  const max = bounds.maxMatchAreaCm2 ?? LOCATE_MAX_MATCH_AREA_CM2;

  return `min ${bounds.minMatchAreaCm2 ?? 0}cm², max ${
    isUnboundedMaxMatchAreaCm2(max) ? 'no limit' : `${max}cm²`
  }`;
}

/**
 * Whether a match's area on `projection` is inside `bounds`. A match whose
 * outline does not land on the plane is kept: an unmeasurable area is not
 * evidence that the match is the wrong size.
 */
export function isMatchWithinAreaBounds(
  match: MatchShape,
  bounds: MatchAreaBounds,
  projection: MatchAreaProjection,
): boolean {
  const areaCm2 = matchAreaCm2(match, projection);

  return areaCm2 === undefined || isMatchAreaWithinBounds(areaCm2, bounds);
}

/**
 * The size rule a ManualPlane locate keeps matches by. Without a projection
 * nothing can be measured, so every match is kept.
 */
export function makeMatchAreaFilter(
  bounds: MatchAreaBounds,
  projection: MatchAreaProjection | undefined,
): (match: MatchShape) => boolean {
  if (!projection) return () => true;

  return (match) => isMatchWithinAreaBounds(match, bounds, projection);
}
